import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { TYPE } from 'theme';
import { RowBetween } from 'components/Row';
import user from 'stores/user';
import { CAPSULE_NAME_SUFFIX, getEditablePartByName, getFullCapsuleName } from 'utils/capsuleUtil';
import { formatHashValue } from 'utils';
import { useSignMessage } from 'wagmi';
import { getOnePieceServerApi } from 'gql-ops';
import simpleDialog from 'stores/simpleDialog';
import { autorun } from 'mobx';
import Button from 'components/Button';
import Modal from 'components/Modal';

const ModalContent = styled.div`
    position: relative;
    width: 330px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    ${({ theme }) => theme.mediaWidth.upToSmall`
        width: 90%;
    `};
`;

const UserNameInput = styled.input`
    width: 100%;
    height: 48px;
    background: #f5f6fa;
    border: none;
    border-radius: 12px;
    padding: 0 16px;
    font-size: 14px;
    flex-grow: 1;
    flex: 1 1 0;

    &::placeholder {
        font-size: 14px;
    }
`;
const UserNameSuffix = styled.span`
    font-size: 14px;
    font-weight: 700;
`;
const SaveButton = styled(Button)`
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 12px;
    width: 100%;
    height: 48px !important;
    font-size: 20px !important;
    margin-top: 30px;
`;

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const EditNameModalContent = ({ isOpen, onClose }: ModalProps) => {
    const { signMessageAsync } = useSignMessage();

    const defaultEditName: string = user?.opsUser?.name
        ? getEditablePartByName(user.opsUser.name)
        : formatHashValue(user?.address) ?? '';
    const [userName, setUserName] = useState<string>(defaultEditName);

    useEffect(
        () =>
            autorun(() => {
                setUserName(user?.opsUser?.name ? getEditablePartByName(user.opsUser.name) : defaultEditName);
            }),
        [defaultEditName],
    );

    const onEditNameInputChange = (e: any) => {
        // limit to 18 chart.
        const value = e.target.value.trimStart().substring(0, 18);
        setUserName(value);
    };

    const isValidUserName = useMemo(() => {
        // const nameRegex = /^[a-zA-Z0-9_\u4e00-\u9fa5]{1,18}$/;
        // return nameRegex.test(userName);
        return userName && userName.length <= 18;
    }, [userName]);

    const getNameErrorMessage = useMemo(() => {
        if (!userName || isValidUserName) return '';
        return 'Only letters, numbers, underscores and Chinese characters are allowed (1-18 characters)';
    }, [userName, isValidUserName]);

    const onSaveEditName = async () => {
        if (!userName || !isValidUserName) return;
        try {
            await getOnePieceServerApi(true).setUserName({
                name: getFullCapsuleName(userName),
                address: user.address,
            });
            await user.refreshOpsUserInfo();
        } catch (error) {
            console.error(error);
            simpleDialog.error({
                title: 'Error',
                text: 'Update capsule name error',
            });
        }
        onClose();
    };
    return (
        <ModalContent>
            <TYPE.main fontSize={20} fontWeight={600} marginBottom="20px" marginTop="20px">
                Change Capsule name
            </TYPE.main>
            <RowBetween gap="12px">
                <UserNameInput placeholder={defaultEditName} value={userName} onChange={onEditNameInputChange} />
                <UserNameSuffix>{CAPSULE_NAME_SUFFIX}</UserNameSuffix>
            </RowBetween>
            <TYPE.desc fontSize={14} color="#E72D2D" alignSelf="flex-start">
                {getNameErrorMessage}
            </TYPE.desc>

            <SaveButton onClick={onSaveEditName} disabled={!userName || !isValidUserName} type="gradient">
                Save
            </SaveButton>
        </ModalContent>
    );
};

const EditNameModal = (props: ModalProps) => {
    return (
        <Modal {...props} shadowProps>
            <EditNameModalContent {...props} />
        </Modal>
    );
};

export default EditNameModal;
